<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import {
  currentUser,
  listUsers,
  listRoles,
  listModules,
  getRolePermissions,
  setUserRole,
  setRolePermission,
  deleteUser,
  getUserEffectivePermissions,
  getUserPermissions,
  setUserPermission,
  updateUser,
  clearUserOverrides,
} from "../db/auth.js";
import ChatAssistant from "../components/ChatAssistant.vue";

const router = useRouter();
const me = computed(() => currentUser());
const loading = ref(false);
const error = ref("");

// Usuarios
const users = ref([]);
const roles = ref([]);
const search = ref("");
const showSuggestions = ref(false);
const hoverIndex = ref(-1);

// Permisos por rol
const modules = ref([]);
const selectedRoleId = ref(null);
const rolePerms = ref([]); // [{ module_id, code, name, can_access }]
const selectedUser = ref(null);
const userPerms = ref([]); // permisos efectivos para el usuario seleccionado

const filteredUsers = computed(() => {
  const q = search.value.trim().toLowerCase();
  return !q
    ? users.value
    : users.value.filter(
      (u) =>
        (u.username || "").toLowerCase().includes(q) ||
        (u.full_name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
    );
});
const suggestions = computed(() => {
  const q = search.value.trim().toLowerCase();
  const base = !q
    ? users.value.slice()
    : users.value.filter(
      (u) =>
        (u.username || "").toLowerCase().includes(q) ||
        (u.full_name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
    );
  return base.slice(0, 8);
});

// Modal de edición
const showEdit = ref(false);
const editUserId = ref(null);
const editForm = ref({
  username: "",
  full_name: "",
  email: "",
  avatar_url: "",
  is_active: true,
});

onMounted(async () => {
  // Restringir acceso: sólo admin o superadmin
  const roleName = me.value?.role || "";
  if (!["admin", "superadmin"].includes(roleName)) {
    router.push("/dashboard");
    return;
  }
  loading.value = true;
  try {
    roles.value = await listRoles();
    users.value = await listUsers();
    modules.value = await listModules();
    // Preseleccionar un rol para edición de permisos
    selectedRoleId.value =
      roles.value.find((r) => r.name === "admin")?.id ||
      roles.value[0]?.id ||
      null;
    if (selectedRoleId.value) {
      rolePerms.value = await getRolePermissions(selectedRoleId.value);
    }
  } catch (e) {
    error.value = e?.message || String(e);
  } finally {
    loading.value = false;
  }
});

async function changeRole(userId, roleId) {
  // Convertir roleId a número para evitar rebotes por tipo
  roleId = Number(roleId);
  const res = await setUserRole(userId, roleId);
  if (!res?.ok) {
    error.value = res?.message || "No se pudo actualizar el rol";
  } else {
    // Actualizar selección de rol en panel izquierdo y refrescar permisos por rol
    selectedRoleId.value = roleId;
    try {
      rolePerms.value = await getRolePermissions(roleId);
    } catch { }
    // Limpiar overrides para que los permisos queden según el rol
    const cleared = await clearUserOverrides(userId);
    if (!cleared?.ok) {
      // Avisar pero continuar para actualizar UI acorde al rol
      await Swal.fire({
        icon: "warning",
        title: "No se pudieron limpiar overrides",
        text:
          (cleared?.message ||
            "Verifica políticas RLS en Supabase para user_permissions") +
          ". Los permisos podrían no reflejar el rol hasta corregir esto.",
      });
    }
    // Actualización optimista en UI antes de recargar desde backend
    const newRole =
      roles.value.find((r) => Number(r.id) === Number(roleId)) || null;
    users.value = users.value.map((u) =>
      u.id === userId
        ? {
          ...u,
          role_id: roleId,
          role_name: newRole?.name || u.role_name || u.role,
        }
        : u
    );
    if (selectedUser.value?.id === userId) {
      selectedUser.value = {
        ...selectedUser.value,
        role_id: roleId,
        role_name:
          newRole?.name ||
          selectedUser.value.role_name ||
          selectedUser.value.role,
      };
    }
    // Optimista: si no hay overrides, los permisos efectivos deben ser los del rol
    try {
      const roleName = String(
        newRole?.name ||
        selectedUser.value?.role_name ||
        selectedUser.value?.role ||
        ""
      )
        .trim()
        .toLowerCase();
      if (roleName === "admin" || roleName === "superadmin") {
        const mods = await listModules();
        userPerms.value = (mods || []).map((m) => ({
          module_id: m.id,
          code: m.code,
          name: m.name,
          can_access: 1,
        }));
      } else {
        userPerms.value = await getRolePermissions(roleId);
      }
    } catch { }
    // Refrescar lista desde backend para garantizar consistencia
    users.value = await listUsers();
    const fresh =
      users.value.find((u) => u.id === userId) || selectedUser.value;
    if (fresh) selectedUser.value = fresh;
    // Recargar permisos: si no se pudieron limpiar overrides, mostrar permisos del rol
    if (cleared?.ok) {
      // Finalmente, cargar permisos efectivos desde backend (rol + overrides)
      const eff = await getUserEffectivePermissions(userId);
      // Fallback: si todo viene en 0 o vacío, aplicar mínimos por rol
      const noPerms =
        !eff ||
        eff.length === 0 ||
        eff.every((e) => Number(e.can_access || 0) === 0);
      if (noPerms) {
        try {
          const mods = await listModules();
          const roleName = String(
            roles.value.find((r) => Number(r.id) === Number(roleId))?.name ||
            selectedUser.value?.role_name ||
            selectedUser.value?.role ||
            ""
          )
            .trim()
            .toLowerCase();
          const roleDefaults = {
            cliente: new Set(["products", "purchases", "menu"]),
            proveedor: new Set([
              "dashboard",
              "purchases",
              "suppliers",
              "inventory",
              "base_purchases",
            ]),
            chef: new Set(["dashboard", "inventory", "initial_inventory"]),
            mesero: new Set([
              "dashboard",
              "sales",
              "customers",
              "base_sales",
              "report_sales",
            ]),
          };
          const allowed = roleDefaults[roleName] || new Set(["dashboard"]);
          userPerms.value = (mods || []).map((m) => ({
            module_id: m.id,
            code: m.code,
            name: m.name,
            can_access: allowed.has(m.code) ? 1 : 0,
          }));
        } catch (e) {
          userPerms.value = eff || [];
        }
      } else {
        userPerms.value = eff;
      }
    } else {
      // Construir permisos por defecto según rol para reflejar el cambio en UI
      try {
        const mods = await listModules();
        const roleName = String(
          roles.value.find((r) => Number(r.id) === Number(roleId))?.name ||
          selectedUser.value?.role_name ||
          selectedUser.value?.role ||
          ""
        )
          .trim()
          .toLowerCase();
        const roleDefaults = {
          cliente: new Set(["products", "purchases", "menu"]),
          proveedor: new Set([
            "dashboard",
            "purchases",
            "suppliers",
            "inventory",
            "base_purchases",
          ]),
          chef: new Set(["dashboard", "inventory", "initial_inventory"]),
          mesero: new Set([
            "dashboard",
            "sales",
            "customers",
            "base_sales",
            "report_sales",
          ]),
        };
        const allowed = roleDefaults[roleName] || new Set(["dashboard"]);
        userPerms.value = (mods || []).map((m) => ({
          module_id: m.id,
          code: m.code,
          name: m.name,
          can_access: allowed.has(m.code) ? 1 : 0,
        }));
      } catch (e) {
        userPerms.value = await getRolePermissions(roleId);
      }
    }
  }
}

function onSearchFocus() {
  showSuggestions.value = true;
}
function onSearchBlur() {
  setTimeout(() => (showSuggestions.value = false), 180);
}
function onSearchInput() {
  showSuggestions.value = true;
}
function onSearchKeydown(e) {
  if (!showSuggestions.value || !suggestions.value.length) return;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    hoverIndex.value = (hoverIndex.value + 1) % suggestions.value.length;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    hoverIndex.value = (hoverIndex.value - 1 + suggestions.value.length) % suggestions.value.length;
  } else if (e.key === "Enter") {
    e.preventDefault();
    const row = suggestions.value[hoverIndex.value >= 0 ? hoverIndex.value : 0];
    if (row) selectSuggestion(row);
  } else if (e.key === "Escape") {
    showSuggestions.value = false;
  }
}
function selectSuggestion(u) {
  search.value = u.full_name || u.username || u.email || "";
  selectedUser.value = u;
  showSuggestions.value = false;
}

async function selectRolePerms(rid) {
  selectedRoleId.value = rid;
  rolePerms.value = await getRolePermissions(rid);
}

async function togglePermission(moduleId, val) {
  if (!selectedRoleId.value) return;
  const res = await setRolePermission(selectedRoleId.value, moduleId, val);
  if (!res?.ok) {
    error.value = res?.message || "No se pudo guardar el permiso";
    // Mostrar alerta si el módulo no existe
    if (res?.message && res.message.includes("no existe")) {
      const perm = rolePerms.value.find((p) => p.module_id === moduleId);
      const moduleName = perm ? perm.name : `Módulo ${moduleId}`;
      await Swal.fire({
        icon: "warning",
        title: "Módulo no disponible",
        html: `<p>El módulo <strong>"${moduleName}"</strong> no existe en la base de datos.</p>
               <p>Este permiso se ignorará hasta que el módulo sea agregado a la tabla <code>modules</code>.</p>`,
        confirmButtonText: "Entendido",
      });
      // Revertir el cambio en la UI
      const row = rolePerms.value.find((p) => p.module_id === moduleId);
      if (row) row.can_access = val ? 0 : 1;
    }
    return;
  }
  // Reflejar cambio en memoria
  const row = rolePerms.value.find((p) => p.module_id === moduleId);
  if (row) row.can_access = val ? 1 : 0;
}

function canDelete(u) {
  const my = me.value;
  // Evitar que un admin se elimine a sí mismo
  return my && my.id !== u.id;
}

async function removeUser(u) {
  if (!canDelete(u)) {
    await Swal.fire({
      icon: "info",
      title: "Acción no permitida",
      text: "No puedes eliminar tu propia cuenta.",
      confirmButtonText: "Entendido",
    });
    return;
  }
  const res = await Swal.fire({
    icon: "warning",
    title: `Eliminar usuario “${u.username}”`,
    text: "Esta acción es permanente. ¿Confirmas?",
    showCancelButton: true,
    confirmButtonColor: "#e74c3c",
    cancelButtonText: "Cancelar",
    confirmButtonText: "Eliminar",
  });
  if (!res.isConfirmed) return;
  const out = await deleteUser(u.id);
  if (!out?.ok) {
    await Swal.fire({
      icon: "error",
      title: "No se pudo eliminar",
      text: out?.message || "Error desconocido",
    });
    return;
  }
  await Swal.fire({
    icon: "success",
    title: "Usuario eliminado",
    timer: 1200,
    showConfirmButton: false,
  });
  users.value = await listUsers();
  if (selectedUser.value?.id === u.id) {
    selectedUser.value = null;
    userPerms.value = [];
  }
}

async function pickUser(u) {
  selectedUser.value = u;
  // Normalizar nombre de rol
  const roleName = String(u.role_name || u.role || "")
    .trim()
    .toLowerCase();
  // Optimista: admin/superadmin base ON en todos, respetando overrides guardados
  if (roleName === "admin" || roleName === "superadmin") {
    try {
      const mods = await listModules();
      // Leer overrides existentes y fusionar sobre base ON
      const overs = await getUserPermissions(u.id);
      const omap = new Map(
        (overs || []).map((r) => [r.module_id, r.can_access])
      );
      userPerms.value = (mods || []).map((m) => ({
        module_id: m.id,
        code: m.code,
        name: m.name,
        can_access:
          omap.has(m.id) && omap.get(m.id) !== null
            ? omap.get(m.id)
              ? 1
              : 0
            : 1,
      }));
      return;
    } catch (e) {
      // Si falla la carga, caer a permisos efectivos reales
    }
  }

  let rows = await getUserEffectivePermissions(u.id);
  // Fallback: si por alguna razón no vienen permisos, poblar con lista de módulos
  if (!rows || rows.length === 0) {
    try {
      const mods = await listModules();
      const base = (mods || []).map((m) => ({
        module_id: m.id,
        code: m.code,
        name: m.name,
        can_access: 0,
      }));
      // Para cliente, asegurar mínimo acceso a products/purchases/menu
      if (roleName === "cliente") {
        const allowed = new Set(["products", "purchases", "menu"]);
        rows = base.map((r) =>
          allowed.has(r.code) ? { ...r, can_access: 1 } : r
        );
      } else {
        rows = base;
      }
    } catch (e) {
      rows = [];
    }
  }
  userPerms.value = rows;
}

async function toggleUserPermission(moduleId, val) {
  if (!selectedUser.value) return;
  const res = await setUserPermission(selectedUser.value.id, moduleId, val);
  if (!res?.ok) {
    error.value =
      (res?.message && String(res.message)) ||
      "No se pudo guardar el permiso del usuario";
    // Alertar para visibilidad inmediata del fallo
    try {
      const perm = userPerms.value.find((p) => p.module_id === moduleId);
      const moduleName = perm ? perm.name : `Módulo ${moduleId}`;
      await Swal.fire({
        icon: "warning",
        title: "Módulo no disponible",
        html: `<p>El módulo <strong>"${moduleName}"</strong> no existe en la base de datos.</p>
               <p>Este permiso se ignorará hasta que el módulo sea agregado a la tabla <code>modules</code>.</p>`,
        confirmButtonText: "Entendido",
      });
    } catch { }
    // Revertir el cambio en la UI
    const row = userPerms.value.find((p) => p.module_id === moduleId);
    if (row) row.can_access = val ? 0 : 1;
    return;
  }
  const row = userPerms.value.find((p) => p.module_id === moduleId);
  if (row) row.can_access = val ? 1 : 0;
}

function openEdit(u) {
  editUserId.value = u.id;
  editForm.value = {
    username: u.username || "",
    full_name: u.full_name || "",
    email: u.email || "",
    avatar_url: u.avatar_url || "",
    is_active: !!u.is_active,
  };
  showEdit.value = true;
}

async function saveEdit() {
  if (!editUserId.value) return;
  const res = await updateUser(editUserId.value, {
    ...editForm.value,
    is_active: !!editForm.value.is_active,
  });
  if (!res?.ok) {
    await Swal.fire({
      icon: "error",
      title: "No se pudo guardar",
      text: res?.message || "Error desconocido",
    });
    return;
  }
  showEdit.value = false;
  users.value = await listUsers();
  if (selectedUser.value?.id === editUserId.value) {
    selectedUser.value =
      users.value.find((x) => x.id === editUserId.value) || selectedUser.value;
    userPerms.value = await getUserEffectivePermissions(editUserId.value);
  }
  await Swal.fire({
    icon: "success",
    title: "Guardado",
    timer: 900,
    showConfirmButton: false,
  });
}

function closeEdit() {
  showEdit.value = false;
}

// Icono por código de módulo (Material Symbols)
function iconFor(code) {
  const c = String(code || "").toLowerCase();
  switch (c) {
    case "dashboard":
      return "dashboard";
    case "inventory":
      return "inventory_2";
    case "purchases":
      return "shopping_cart";
    case "sales":
      return "point_of_sale";
    case "initial_inventory":
      return "playlist_add";
    case "taxes":
      return "request_quote";
    case "suppliers":
      return "local_shipping";
    case "customers":
      return "group";
    case "report_sales":
      return "analytics";
    case "daily_sales":
      return "calendar_today";
    case "daily_profits":
      return "trending_up";
    case "base_sales":
      return "dataset";
    case "base_purchases":
      return "dataset";
    default:
      return "tune";
  }
}

// Generar avatar por defecto basado en iniciales
function defaultAvatar(user) {
  const name = user.full_name || user.username || "?";
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const colors = [
    "#667eea",
    "#764ba2",
    "#f093fb",
    "#4facfe",
    "#00f2fe",
    "#43e97b",
    "#38f9d7",
    "#fa709a",
    "#fee140",
  ];
  const colorIndex = (user.id || 0) % colors.length;
  const bgColor = colors[colorIndex];

  // Crear un SVG simple con las iniciales
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
      <rect width="100" height="100" fill="${bgColor}"/>
      <text x="50" y="50" text-anchor="middle" dominant-baseline="central" 
            font-family="Arial, sans-serif" font-size="40" font-weight="bold" fill="white">
        ${initials}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
}
</script>

<template>
  <main class="users-wrap">
    <header class="head">
      <h1 class="title">Usuarios</h1>
      <p class="subtitle">Gestión de roles y permisos (solo admin)</p>
    </header>

    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="loading" class="muted">Cargando…</p>

    <section v-if="!loading" class="grid">
      <div class="card">
        <div class="card-head" :class="{ 'suggestions-open': showSuggestions && suggestions.length }">
          <h2>Usuarios</h2>
          <div class="search-wrap" title="Buscar usuarios">
            <span class="material-symbols-rounded search-icon" aria-hidden="true">search</span>
            <input class="search-input" v-model="search" placeholder="Buscar por nombre o email" @focus="onSearchFocus"
              @blur="onSearchBlur" @input="onSearchInput" @keydown="onSearchKeydown" />
            <button v-if="search" class="clear-btn" @click="search = ''" aria-label="Limpiar búsqueda">
              <span class="material-symbols-rounded" aria-hidden="true">close</span>
            </button>
            <div class="suggestions" v-if="showSuggestions && suggestions.length">
              <button v-for="(s, idx) in suggestions" :key="s.id" class="suggestion-item"
                :class="{ active: idx === hoverIndex }" type="button" @mouseenter="hoverIndex = idx"
                @mousedown.prevent="selectSuggestion(s)">
                <img :src="s.avatar_url || defaultAvatar(s)" alt="avatar" class="suggestion-thumb" />
                <span class="suggestion-name">{{ s.full_name || s.username }}</span>
                <span class="suggestion-sku">{{ s.email || '-' }}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="table-wrap">
          <table class="table epic">
            <thead>
              <tr>
                <th>Avatar</th>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in filteredUsers" :key="u.id" :class="{ selected: selectedUser?.id === u.id }"
                @click="pickUser(u)" title="Seleccionar para editar permisos">
                <td>
                  <img :src="u.avatar_url || defaultAvatar(u)" alt="avatar" class="avatar" />
                </td>
                <td class="mono">{{ u.username }}</td>
                <td>{{ u.full_name || "-" }}</td>
                <td>{{ u.email || "-" }}</td>
                <td>
                  <select :value="u.role_id ??
                    roles.find((r) => r.name === (u.role_name || u.role))
                      ?.id ??
                    ''
                    " @change.stop="changeRole(u.id, Number($event.target.value))" @click.stop @mousedown.stop
                    class="role-select">
                    <option v-for="r in roles" :key="r.id" :value="r.id">
                      {{ r.name }}
                    </option>
                  </select>
                </td>
                <td>
                  <span class="badge" :class="u.is_active ? 'ok' : 'ko'">{{
                    u.is_active ? "Activo" : "Inactivo"
                  }}</span>
                  <span class="badge" :class="u.is_verified ? 'ok' : 'ko'">{{
                    u.is_verified ? "Verificado" : "No verificado"
                  }}</span>
                </td>
                <td>
                  <button class="btn primary" @click.stop="openEdit(u)">
                    <span class="material-symbols-rounded" aria-hidden="true">edit</span>
                  </button>
                  <button class="btn danger" :disabled="!canDelete(u)" @click.stop="removeUser(u)">
                    <span class="material-symbols-rounded" aria-hidden="true">delete</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="card">
        <div class="card-head">
          <h2>Permisos por usuario</h2>
          <div v-if="selectedUser" class="selected-info">
            <span class="mono">{{ selectedUser.username }}</span>
            <span class="muted">•</span>
            <span class="muted small">{{
              selectedUser.email || "sin email"
            }}</span>
          </div>
          <div v-else class="muted small">
            Selecciona un usuario de la tabla
          </div>
        </div>
        <div v-if="selectedUser" class="perm-list">
          <div v-for="p in userPerms" :key="p.module_id" class="perm-row" :title="p.code">
            <div class="perm-left">
              <span class="material-symbols-rounded module-icon" aria-hidden="true">{{ iconFor(p.code) }}</span>
              <label class="perm-label">
                <input class="switch" type="checkbox" :checked="!!p.can_access" @change="
                  toggleUserPermission(p.module_id, $event.target.checked)
                  " />
                <span>{{ p.name }}</span>
              </label>
            </div>
            <span class="code-chip mono">{{ p.code }}</span>
          </div>
        </div>
      </div>
    </section>
  </main>

  <!-- Modal de edición de usuario -->
  <div v-if="showEdit" class="modal-overlay" @click.self="closeEdit" role="dialog" aria-modal="true">
    <div class="modal">
      <div class="modal-head">
        <h3>Editar usuario</h3>
        <button class="icon-btn" @click="closeEdit" aria-label="Cerrar">
          <span class="material-symbols-rounded" aria-hidden="true">close</span>
        </button>
      </div>
      <div class="form-grid">
        <label>
          <span class="label">Usuario</span>
          <input type="text" v-model="editForm.username" />
        </label>
        <label>
          <span class="label">Nombre</span>
          <input type="text" v-model="editForm.full_name" />
        </label>
        <label>
          <span class="label">Email</span>
          <input type="text" v-model="editForm.email" />
        </label>
        <label>
          <span class="label">Avatar URL</span>
          <input type="text" v-model="editForm.avatar_url" />
        </label>
        <label class="toggle-line">
          <input class="switch" type="checkbox" v-model="editForm.is_active" />
          <span class="label">Activo</span>
        </label>
      </div>
      <div class="modal-actions">
        <button class="btn" @click="closeEdit">
          <span class="material-symbols-rounded" aria-hidden="true">close</span>
          Cancelar
        </button>
        <button class="btn primary" @click="saveEdit">
          <span class="material-symbols-rounded" aria-hidden="true">check</span>
          Guardar
        </button>
      </div>
    </div>
  </div>
  <ChatAssistant />
</template>

<style scoped>
.users-wrap {
  --surface-2: rgba(255, 255, 255, 0.65);
  display: grid;
  gap: 1.5rem;
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  overflow-x: hidden;
  isolation: isolate;
  box-sizing: border-box;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
}

.head {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.title {
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #C6923D, #DFC05E);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  margin: 0;
}

.title::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: -8px;
  width: 60px;
  height: 4px;
  border-radius: 4px;
  background: linear-gradient(90deg, #C6923D, #DFC05E);
  box-shadow: 0 2px 8px rgba(198, 146, 61, 0.3);
}

.subtitle {
  color: var(--muted);
  font-size: 1rem;
  margin: 0.5rem 0 0 0;
}

.grid {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 0.9fr);
  gap: 2rem;
  justify-content: center;
  align-items: start;
  width: 100%;
}

.card {
  display: grid;
  gap: 0.8rem;
  border: 1px solid var(--border);
  border-radius: 24px;
  padding: 1.5rem;
  background: var(--surface);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
}

.card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #C6923D, #DFC05E, #C6923D);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.card:hover {
  box-shadow: 0 12px 40px rgba(198, 146, 61, 0.15);
  transform: translateY(-4px);
  border-color: rgba(198, 146, 61, 0.3);
}

.card:hover::before {
  opacity: 1;
}

@media (prefers-color-scheme: dark) {
  .card {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
  }
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 0.5rem;
  border-bottom: 2px solid transparent;
  border-image: linear-gradient(90deg, rgba(198, 146, 61, 0.3), transparent);
  border-image-slice: 1;
  flex-wrap: wrap;
}

.card-head h2 {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #C6923D, #DFC05E);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  letter-spacing: -0.02em;
}

.table-wrap {
  width: 100%;
  overflow: auto;
  border-radius: 16px;
}

.table {
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;
  /* permite scroll si el card es estrecho */
}

.table th,
.table td {
  text-align: left;
  padding: 0.5rem;
  border-bottom: none;
}

.table.epic thead th {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.12), rgba(223, 192, 94, 0.12));
  color: var(--text);
  border: none;
  border-bottom: 2px solid rgba(198, 146, 61, 0.3);
  border-radius: 0;
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 1rem 1.25rem;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  backdrop-filter: blur(10px);
}

.table.epic thead th:first-child {
  border-top-left-radius: 12px;
}

.table.epic thead th:last-child {
  border-top-right-radius: 12px;
}

.table tbody td {
  padding: 1rem 1.25rem;
  border: none;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}

.table tbody tr {
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  border-left: 4px solid transparent;
}

.table tbody tr:hover {
  background: rgba(198, 146, 61, 0.04);
  border-left-color: #C6923D;
}

.table tbody tr.selected {
  background: rgba(198, 146, 61, 0.08);
  border-left-color: #C6923D;
}

.table tbody tr:nth-child(odd) {
  background: transparent;
}

@media (prefers-color-scheme: dark) {
  .table.epic thead th {
    background: rgba(139, 92, 246, 0.15);
    color: #e5e7eb;
  }
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid rgba(198, 146, 61, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.avatar:hover {
  transform: scale(1.1);
  border-color: #C6923D;
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-weight: 600;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-right: 0.5rem;
  border: 2px solid;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  transition: all 0.3s ease;
}

.badge.ok {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border-color: rgba(16, 185, 129, 0.3);
}

.badge.ok:hover {
  background: rgba(16, 185, 129, 0.15);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

.badge.ko {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.3);
}

.badge.ko:hover {
  background: rgba(239, 68, 68, 0.15);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
}

.perm-list {
  display: grid;
  gap: 0.6rem;
  grid-template-columns: repeat(2, minmax(200px, 1fr));
}

.perm-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border: 2px solid var(--border);
  border-radius: 16px;
  background: var(--surface);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: 1rem;
  flex-wrap: nowrap;
  max-width: 100%;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
}

.perm-row::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #C6923D, #DFC05E);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.perm-row:hover {
  border-color: rgba(198, 146, 61, 0.3);
  box-shadow: 0 8px 24px rgba(198, 146, 61, 0.12);
  transform: translateY(-2px);
  background: rgba(198, 146, 61, 0.04);
}

.perm-row:hover::before {
  opacity: 1;
}

@media (prefers-color-scheme: dark) {
  .perm-row {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
  }
}

.perm-left {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.perm-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
}

.module-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  color: white;
  background: linear-gradient(135deg, #C6923D, #DFC05E);
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
  transition: all 0.3s ease;
}

.perm-row:hover .module-icon {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 6px 16px rgba(198, 146, 61, 0.4);
}

.code-chip {
  display: inline-block;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  border: 1px dashed var(--border);
  color: var(--muted);
  background: rgba(0, 0, 0, 0.03);
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  min-width: 140px;
  justify-content: center;
}

.btn:not(.primary):not(.danger) {
  background: var(--surface);
  color: var(--text);
  border: 2px solid var(--border);
  box-shadow: none;
}

.btn:not(.primary):not(.danger):hover {
  background: rgba(198, 146, 61, 0.08);
  border-color: #C6923D;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.btn.primary {
  background: linear-gradient(135deg, #C6923D, #DFC05E);
  color: white;
  box-shadow: 0 4px 16px rgba(198, 146, 61, 0.3);
  position: relative;
  overflow: hidden;
  border: none;
}

.btn.primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(198, 146, 61, 0.4);
}

.btn.primary:hover::before {
  left: 100%;
}

.btn.primary:active {
  transform: translateY(0);
}

.btn.danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  box-shadow: 0 4px 16px rgba(239, 68, 68, 0.3);
  border: none;
  position: relative;
  overflow: hidden;
}

.btn.danger::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn.danger:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(239, 68, 68, 0.4);
}

.btn.danger:hover::before {
  left: 100%;
}

.btn.danger:active {
  transform: translateY(0);
}

.btn.danger:disabled,
.btn.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.selected-info {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.error {
  color: #ff6b6b;
}

.muted {
  color: var(--muted);
}

.small {
  font-size: 0.9rem;
}

/* Prioridad visual: el card izquierdo debe sobreponerse para que sus opciones no se tapen */
.grid>.card:nth-child(1) {
  position: relative;
  z-index: 5;
}

.grid>.card:nth-child(2) {
  position: relative;
  z-index: 1;
  max-width: 500px;
}

/* Barra de búsqueda moderna */
.search-wrap {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(198, 146, 61, 0.04);
  border: 2px solid transparent;
  border-radius: 16px;
  padding: 0.75rem 1rem;
  min-width: 280px;
  max-width: 400px;
  flex: 0 1 400px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  position: relative;
  transition: all 0.3s ease;
}

.search-wrap:focus-within {
  background: var(--surface);
  box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08);
  border-color: #C6923D;
}

.search-icon {
  color: var(--muted);
  font-size: 22px;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text);
  padding: 0.5rem;
  font-size: 0.95rem;
  font-weight: 500;
}

.search-input::placeholder {
  color: var(--muted);
  font-weight: 400;
}

.clear-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: none;
  background: rgba(198, 146, 61, 0.08);
  color: var(--muted);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.clear-btn:hover {
  background: rgba(198, 146, 61, 0.15);
  color: #C6923D;
  transform: scale(1.05);
}

@media (prefers-color-scheme: dark) {
  .search-wrap {
    background: rgba(139, 92, 246, 0.08);
    border-color: rgba(139, 92, 246, 0.15);
  }
  
  .search-wrap:focus-within {
    background: rgba(255, 255, 255, 0.06);
    border-color: #8b5cf6;
  }
}

.suggestions {
  position: absolute;
  left: 0;
  top: calc(100% + 10px);
  width: 100%;
  background: #1a1d29;
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  z-index: 9999;
  overflow: hidden;
  backdrop-filter: blur(20px);
  animation: slideDown 0.2s ease;
}

@media (prefers-color-scheme: light) {
  .suggestions {
    background: #ffffff;
    border-color: var(--border);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.suggestion-item {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  gap: 0.875rem;
  align-items: center;
  padding: 0.875rem 1rem;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #e5e7eb;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s ease;
  text-align: left;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover,
.suggestion-item.active {
  background: linear-gradient(90deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%);
  padding-left: 1.25rem;
}

@media (prefers-color-scheme: light) {
  .suggestion-item {
    color: var(--text);
    border-bottom-color: var(--border);
  }
  
  .suggestion-item:hover,
  .suggestion-item.active {
    background: linear-gradient(90deg, rgba(109, 93, 246, 0.08) 0%, rgba(102, 126, 234, 0.05) 100%);
  }
}

.suggestion-thumb {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 10px;
  border: 2px solid rgba(139, 92, 246, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

@media (prefers-color-scheme: light) {
  .suggestion-thumb {
    border-color: var(--border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.suggestion-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.suggestion-sku {
  color: #9ca3af;
  font-size: 0.875rem;
}

@media (prefers-color-scheme: light) {
  .suggestion-sku {
    color: var(--muted);
  }
}

input[type="text"],
input[type="search"],
select {
  border: 2px solid rgba(198, 146, 61, 0.15);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  background: var(--surface);
  color: var(--text);
  outline: none;
  font-weight: 500;
  transition: all 0.3s ease;
  color-scheme: dark;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

input[type="text"]:hover,
input[type="search"]:hover,
select:hover {
  border-color: rgba(198, 146, 61, 0.3);
  box-shadow: 0 4px 8px rgba(198, 146, 61, 0.1);
}

input[type="text"]:focus,
input[type="search"]:focus,
select:focus {
  box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.15), 0 4px 12px rgba(198, 146, 61, 0.2);
  border-color: #C6923D;
  transform: translateY(-1px);
}

@media (prefers-color-scheme: dark) {
  input[type="text"],
  input[type="search"],
  select {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(198, 146, 61, 0.25);
  }
}

/* Mejorar visibilidad del menú desplegable en modo oscuro */
@media (prefers-color-scheme: dark) {
  .role-select {
    background-color: #0f172a;
    /* gris muy oscuro */
    border-color: #334155;
    /* contorno tenue */
    color: #e5e7eb;
    /* texto claro */
    background-image: linear-gradient(45deg, transparent 50%, #94a3b8 50%),
      linear-gradient(135deg, #94a3b8 50%, transparent 50%);
    background-position: calc(100% - 18px) calc(50% - 2px),
      calc(100% - 12px) calc(50% - 2px);
    background-size: 6px 6px, 6px 6px;
    background-repeat: no-repeat;
  }

  .role-select option,
  .role-select optgroup {
    background-color: #0f172a;
    color: #e5e7eb;
  }

  .role-select option:hover,
  .role-select option:checked {
    background-color: #1f2937;
    /* hover/selected */
    color: #ffffff;
  }
}

/* Toggle switch moderno */
.switch {
  appearance: none;
  width: 44px;
  height: 24px;
  border-radius: 999px;
  border: 2px solid var(--border);
  background: rgba(0, 0, 0, 0.1);
  position: relative;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
}

.switch:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.2);
  border-color: #C6923D;
}

.switch::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.switch:checked {
  background: linear-gradient(135deg, #10b981, #059669);
  border-color: #10b981;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.switch:checked::after {
  transform: translateX(20px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
}

.switch:hover {
  border-color: rgba(198, 146, 61, 0.4);
}

.switch:checked:hover {
  border-color: #10b981;
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
}

@media (prefers-color-scheme: dark) {
  .switch {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.1);
  }
}

/* Tipografía más compacta en el card derecho */
.grid>.card:nth-child(2) h2 {
  font-size: 1.05rem;
}

.grid>.card:nth-child(2) .perm-label span {
  font-size: 0.9rem;
}

.grid>.card:nth-child(2) .small {
  font-size: 0.8rem;
}

.grid>.card:nth-child(2) {
  max-width: 460px;
}

/* Responsivo: en pantallas medianas apilar y evitar solapamientos */
@media (max-width: 1280px) {
  .grid {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }

  .grid>.card:nth-child(1),
  .grid>.card:nth-child(2) {
    z-index: auto;
  }

  .card-head {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 0.5rem;
  }

  .search-wrap {
    width: 100%;
    max-width: none;
    flex: 1 1 auto;
  }
}


@media (max-width: 1200px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1100px) {
  .perm-list {
    grid-template-columns: 1fr;
  }
}

/* Modal moderno con animaciones */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: grid;
  place-items: center;
  z-index: 99999;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal {
  width: 100%;
  max-width: 680px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 2px solid rgba(198, 146, 61, 0.1);
  border-radius: 24px;
  box-shadow: 0 25px 80px rgba(198, 146, 61, 0.25), 0 0 1px rgba(0, 0, 0, 0.1);
  display: grid;
  gap: 0;
  padding: 0;
  max-height: 90vh;
  grid-template-rows: auto 1fr auto;
  overflow: hidden;
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(40px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@media (prefers-color-scheme: dark) {
  .modal {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    border: 2px solid rgba(198, 146, 61, 0.3);
  }
}

.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 2rem 1.5rem 2rem;
  border-bottom: 3px solid transparent;
  border-image: linear-gradient(90deg, #C6923D, #DFC05E, #C6923D);
  border-image-slice: 1;
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.08), rgba(223, 192, 94, 0.08));
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 10;
}

@media (prefers-color-scheme: dark) {
  .modal-head {
    background: linear-gradient(135deg, rgba(198, 146, 61, 0.15), rgba(223, 192, 94, 0.15));
  }
}

.modal-head h3 {
  margin: 0;
  background: linear-gradient(135deg, #C6923D, #DFC05E);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-head h3::before {
  content: '👤';
  font-size: 2rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem 1.5rem;
  overflow-y: auto;
  padding: 2rem;
  overscroll-behavior: contain;
}

.form-grid label {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-grid .label {
  font-size: 0.9rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
  margin-bottom: 0.25rem;
}

@media (prefers-color-scheme: dark) {
  .form-grid .label {
    color: #94a3b8;
  }
}

.form-grid input[type="text"] {
  padding: 0.875rem 1.25rem;
  border: 2px solid rgba(198, 146, 61, 0.15);
  border-radius: 12px;
  background: white;
  color: #1e293b;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

@media (prefers-color-scheme: dark) {
  .form-grid input[type="text"] {
    background: rgba(255, 255, 255, 0.08);
    color: #f1f5f9;
    border-color: rgba(198, 146, 61, 0.25);
  }
}

.form-grid input[type="text"]:hover {
  border-color: rgba(198, 146, 61, 0.3);
  box-shadow: 0 4px 8px rgba(198, 146, 61, 0.1);
}

.form-grid input[type="text"]:focus {
  outline: none;
  border-color: #C6923D;
  box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.15), 0 4px 12px rgba(198, 146, 61, 0.2);
  transform: translateY(-1px);
}

.toggle-line {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  grid-column: 1 / -1;
  padding: 1rem;
  background: rgba(198, 146, 61, 0.04);
  border-radius: 12px;
  border: 2px solid rgba(198, 146, 61, 0.1);
}

.toggle-line .label {
  font-weight: 600;
  color: var(--text);
  text-transform: none;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.05), rgba(223, 192, 94, 0.05));
  border-radius: 0 0 22px 22px;
  position: sticky;
  bottom: 0;
  backdrop-filter: blur(10px);
  z-index: 10;
}

@media (prefers-color-scheme: dark) {
  .modal-actions {
    background: linear-gradient(135deg, rgba(198, 146, 61, 0.1), rgba(223, 192, 94, 0.1));
  }
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  transition: all 0.3s;
}

.icon-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  transform: scale(1.1);
}

/* Responsive del formulario de edición */
@media (max-width: 800px) {
  .modal {
    max-width: 96vw;
    max-height: 95vh;
  }
  
  .modal-head {
    padding: 1.5rem 1rem;
  }
  
  .modal-head h3 {
    font-size: 1.5rem;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
    padding: 1.5rem 1rem;
  }
  
  .modal-actions {
    padding: 1rem;
    flex-wrap: wrap;
  }
  
  .btn {
    width: 100%;
    min-width: auto;
  }
}

@media (max-width: 768px) {
  .users-wrap {
    padding: 1rem;
  }
  
  .title {
    font-size: 1.75rem;
  }
  
  .card {
    padding: 1rem;
  }
  
  .search-wrap {
    min-width: 100%;
    max-width: 100%;
  }
}
</style>

<script>
// Fallback de avatar simple
export default {
  methods: {
    defaultAvatar(u) {
      const name = (u.full_name || u.username || "User").trim();
      const initials =
        name
          .split(/[\s_]+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((s) => s[0].toUpperCase())
          .join("") || "U";
      const svg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect width="100%" height="100%" rx="32" fill="#E6F7F5"/><text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" font-family="Outfit, sans-serif" font-size="28" fill="#00c2a8">${initials}</text></svg>`;
      return `data:image/svg+xml;base64,${btoa(svg)}`;
    },
  },
};
</script>
